import _ from 'lodash-es';

export function equalsIgnoreCase(s1: string, s2: string): boolean {
	if (_.isNil(s1)) {
		return _.isNil(s2);
	}
	if (_.isNil(s2)) {
		return false;
	}
	return s1.toLowerCase() === s2.toLowerCase();
}

/**
 * Returns true if the given string is null, undefined, or contains only
 * whitespace characters. Returns false otherwise.
 * @param value A string to evaluate.
 */
export function isNilOrWhitespace(value: string): boolean {
	return _.isNil(value) || _.isEmpty(value.trim());
}

/**
 * Returns the width in pixels of a string rendered in an HTMLElement, assuming
 * the element's CSS properties are applied. The original use case for this was
 * to determine heights for review notifications to achieve the desired visual
 * design. It is assumed that the HTMLELement is in the DOM, but optionally not
 * visible.
 */
export function visualLength(s: string, ruler: HTMLElement): number {
	ruler.innerHTML = s;
	return ruler.offsetWidth;
}

export const nameof = <T>(name: keyof T): string => name.toString();

/**
 * ADJQ-1423: ensures a url has a protocol (defaults to http) and that it is a proper URL.
 * Otherwise returns null on the assumption it was a placeholder value in jforce that should
 * be ignored in Navigator.
 */
export function convertToValidWebsiteUrl(candidateURL: string): string {
	candidateURL = candidateURL ? candidateURL.trim() : null;
	if (candidateURL) {
		if (candidateURL.search(/^https?:\/\/[.\S]+[.][.\S]+$/gi) === 0) {
			// it looks like a url (has at least 1 ., and no whitespace) and a protocol, so return it as-is
			return candidateURL;
		} else if (candidateURL.search(/^[.\S]+[.][.\S]+$/g) > 0) {
			// Tack on a protocol to something that otherwise looks like a url
			return 'http://' + candidateURL; // ... but add a protocol, defaulting to http.
		} else {
			return null; // Was likely "N/A", "n/a", "http://Website not provided" (w/ or w/out protocol) (ADJQ-1423)
		}
	} else {
		return null;
	}
}

/**
 * Returns the first instance of a number in a string.
 * Converts the output to a number.
 */
export function extractNumber(s: string): number {
	return Number(s.replace(/[^0-9]*([0-9]+).*/, '$1'));
}

/**
 * Only handles when running in browser.
 * We could implement serverside if needed using library like 'he'.
 */
let htmlDecoderParser: DOMParser;
const htmlEntityPat = /&(([-_a-z0-9]+)|(#x?[0-9a-f]+));/gi;

export function decodeHtmlEntities(s: string): string {
	return s.replace(htmlEntityPat, (match) => {
		if (!htmlDecoderParser) {
			htmlDecoderParser = new DOMParser();
		}
		const parsed: Document = htmlDecoderParser.parseFromString(
			match,
			'text/html'
		);
		return parsed.body.textContent;
	});
}

export function splitAtIndex(s: string, i: number): string[] {
	return [s.slice(0, i), s.slice(i)];
}

/**
 * Converts camelCase / PascalCase to a readable string.
 * - Inserts spaces before camelCase boundaries
 * - Makes all words after the first lowercase
 * - Capitalizes only the first character of the entire result
 */
export function camelCaseToReadable(input: string): string {
	if (!input) return input;

	// 1) Insert spaces before capitals that follow lowercase/digit
	const withSpaces = input.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

	// 2) Lowercase all characters (makes 2nd word start lowercase, etc.)
	const lower = withSpaces.toLowerCase();

	// 3) Capitalize only the first character of the entire string
	return lower.charAt(0).toUpperCase() + lower.slice(1);
}
